// Procedural SVG card art. Deterministic per cardId — same id, same art.
// Used as a fallback when no `imageUrl` (i.e. ComfyUI was not configured).
// Five visual families chosen by keyword match against the cardId:
// flow (logs/dns/firewall), host (endpoint/memory), network (hunting/topology),
// people (uba/deception/education/crisis), shield (isolation/threat-intel).
// Stage cards and inject cards have their own family.

interface Props {
  cardId: string;
  kind?: "procedure" | "stage" | "inject";
  height?: number;
}

// Brand-aligned palette: copper structural accent on near-black, no glows.
const ACCENT = "#c8945c";
const ACCENT_2 = "#a57040";
const TEAL = "#3a3a3a";

function familyFor(id: string, kind: Props["kind"]): string {
  if (kind === "stage") {
    if (id.startsWith("ic.")) return "stage-ic";
    if (id.startsWith("pe.")) return "stage-pe";
    if (id.startsWith("ce.")) return "stage-ce";
    if (id.startsWith("p.")) return "stage-p";
  }
  if (kind === "inject") return "inject";
  // procedure heuristics
  if (/log|dns|firewall|siem/.test(id)) return "flow";
  if (/endpoint|memory|server/.test(id)) return "host";
  if (/network|threat-hunt|threat-intel/.test(id)) return "net";
  if (/user|deception|education|crisis|behavior/.test(id)) return "people";
  if (/isolat|protection/.test(id)) return "shield";
  return "flow";
}

// Deterministic 0..1 from id.
function hash01(s: string, salt = 0): number {
  let h = 0x811c9dc5 ^ salt;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return (h % 10000) / 10000;
}

export function CardArt({ cardId, kind = "procedure", height = 96 }: Props) {
  const family = familyFor(cardId, kind);
  const W = 240;
  const H = 96;
  const r1 = hash01(cardId, 1);
  const r2 = hash01(cardId, 2);
  const r3 = hash01(cardId, 3);
  const gradId = `g-${cardId.replace(/[^a-z0-9]/gi, "")}`;

  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMidYMid slice"
      role="img"
      aria-label={cardId}
      style={{ display: "block", borderRadius: 6 }}
    >
      <defs>
        <pattern id={`grid-${gradId}`} width="14" height="14" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="0.6" fill="rgba(239,239,239,0.06)" />
        </pattern>
      </defs>
      <rect width={W} height={H} fill="#0c0c0c" />
      <rect width={W} height={H} fill={`url(#grid-${gradId})`} />
      {family === "flow" && <FlowMotif r1={r1} r2={r2} r3={r3} />}
      {family === "host" && <HostMotif r1={r1} r2={r2} />}
      {family === "net" && <NetMotif r1={r1} r2={r2} r3={r3} />}
      {family === "people" && <PeopleMotif r1={r1} />}
      {family === "shield" && <ShieldMotif r1={r1} />}
      {family === "stage-ic" && <StageIcMotif r1={r1} r2={r2} />}
      {family === "stage-pe" && <StagePeMotif r1={r1} />}
      {family === "stage-ce" && <StageCeMotif r1={r1} />}
      {family === "stage-p" && <StagePMotif r1={r1} />}
      {family === "inject" && <InjectMotif r1={r1} />}
      {/* small bottom-left tick — brand structural accent */}
      <line x1="0" y1={H - 1} x2="32" y2={H - 1} stroke={ACCENT} strokeWidth="1" />
    </svg>
  );
}

function FlowMotif({ r1, r2, r3 }: { r1: number; r2: number; r3: number }) {
  // Streaming log lines
  const baseY = 20 + r1 * 10;
  return (
    <g>
      {Array.from({ length: 6 }, (_, i) => {
        const y = baseY + i * 10;
        const w = 60 + ((i * 37 + r2 * 1000) % 150);
        const x = (i * 11 + r3 * 80) % 60;
        const isHot = i === Math.floor(r1 * 6);
        return (
          <rect key={i} x={x} y={y} width={w} height={3} rx={1.5} fill={isHot ? ACCENT : "rgba(170,200,220,0.35)"} />
        );
      })}
      <circle cx={32} cy={baseY + Math.floor(r1 * 6) * 10 + 1.5} r={6} fill="none" stroke={ACCENT} strokeWidth="1.5" />
    </g>
  );
}

function HostMotif({ r1, r2 }: { r1: number; r2: number }) {
  // Isometric chassis
  const cx = 120;
  const cy = 50;
  return (
    <g transform={`translate(${cx},${cy})`}>
      <polygon points="-50,-18 0,-30 50,-18 0,-6" fill="#1c2c3c" stroke={TEAL} strokeWidth="1" />
      <polygon points="-50,-18 -50,18 0,30 0,-6" fill="#0e1620" stroke={TEAL} strokeWidth="1" />
      <polygon points="0,-6 0,30 50,18 50,-18" fill="#14202c" stroke={TEAL} strokeWidth="1" />
      <circle cx={-30 + r1 * 60} cy={6} r="3" fill={ACCENT} />
      <rect x={-44} y={-2} width={20 + r2 * 40} height={1.5} fill="rgba(170,200,220,0.35)" />
    </g>
  );
}

function NetMotif({ r1, r2, r3 }: { r1: number; r2: number; r3: number }) {
  const nodes = [
    { x: 40, y: 30 },
    { x: 100, y: 60 },
    { x: 160, y: 25 },
    { x: 200, y: 70 },
    { x: 60, y: 70 },
  ];
  const hot = Math.floor(r1 * nodes.length);
  return (
    <g>
      {nodes.map((a, i) =>
        nodes
          .slice(i + 1)
          .map((b, j) => (
            <line
              key={`${i}-${j}`}
              x1={a.x}
              y1={a.y}
              x2={b.x}
              y2={b.y}
              stroke={(i + j + Math.floor(r2 * 3)) % 5 === 0 ? ACCENT : "rgba(170,200,220,0.18)"}
              strokeWidth={(i + j) % 5 === 0 ? 1.5 : 0.7}
            />
          )),
      )}
      {nodes.map((n, i) => (
        <circle
          key={i}
          cx={n.x}
          cy={n.y}
          r={i === hot ? 5 : 3}
          fill={i === hot ? ACCENT : TEAL}
          opacity={i === hot ? 1 : 0.7 + r3 * 0.1}
        />
      ))}
    </g>
  );
}

function PeopleMotif({ r1 }: { r1: number }) {
  // Three avatar silhouettes, one highlighted
  const hot = Math.floor(r1 * 3);
  return (
    <g transform="translate(60,30)">
      {[0, 1, 2].map((i) => (
        <g key={i} transform={`translate(${i * 50},0)`}>
          <circle cx={20} cy={12} r="9" fill={i === hot ? ACCENT : "rgba(170,200,220,0.25)"} />
          <path
            d="M2 40 Q 20 22 38 40"
            fill="none"
            stroke={i === hot ? ACCENT : "rgba(170,200,220,0.25)"}
            strokeWidth="2.5"
          />
        </g>
      ))}
    </g>
  );
}

function ShieldMotif({ r1 }: { r1: number }) {
  return (
    <g transform="translate(120,48)">
      <path
        d="M0 -32 L 26 -22 L 26 8 Q 26 26 0 32 Q -26 26 -26 8 L -26 -22 Z"
        fill="rgba(42,163,184,0.15)"
        stroke={ACCENT}
        strokeWidth="1.5"
      />
      <circle cx={0} cy={-2} r={6 + r1 * 4} fill={ACCENT} opacity="0.55" />
      <path d="M-8 -2 L -2 6 L 10 -8" fill="none" stroke="#0a1014" strokeWidth="2.5" strokeLinecap="round" />
    </g>
  );
}

function StageIcMotif({ r1, r2 }: { r1: number; r2: number }) {
  // open envelope with hook
  return (
    <g transform="translate(120,48)">
      <rect x="-40" y="-22" width="80" height="44" fill="#0e1620" stroke={ACCENT_2} strokeWidth="1.5" />
      <path d="M-40 -22 L 0 8 L 40 -22" fill="none" stroke={ACCENT_2} strokeWidth="1.5" />
      <path
        d={`M 0 8 Q ${10 + r1 * 8} ${20 + r2 * 8} ${-4} ${30 + r2 * 4}`}
        fill="none"
        stroke={ACCENT}
        strokeWidth="2"
      />
      <circle cx={-4} cy={30 + r2 * 4} r="3" fill={ACCENT} />
    </g>
  );
}

function StagePeMotif({ r1 }: { r1: number }) {
  // ascending arrow chain
  return (
    <g transform="translate(0,72)">
      {[0, 1, 2, 3].map((i) => (
        <g key={i} transform={`translate(${30 + i * 50},${-i * 12 - r1 * 8})`}>
          <rect x={0} y={0} width={28} height={18} rx={2} fill="#1c2c3c" stroke={TEAL} strokeWidth="1" />
          <path
            d={`M 38 9 L 50 9 M 46 5 L 50 9 L 46 13`}
            fill="none"
            stroke={ACCENT}
            strokeWidth="1.5"
          />
        </g>
      ))}
    </g>
  );
}

function StageCeMotif({ r1 }: { r1: number }) {
  // outbound arc through wall
  return (
    <g>
      <rect x="100" y="10" width="3" height="76" fill="rgba(170,200,220,0.4)" />
      <rect x="115" y="10" width="3" height="76" fill="rgba(170,200,220,0.4)" />
      <path
        d={`M 20 ${48 + r1 * 8} Q 110 ${20 + r1 * 30} 220 ${48 - r1 * 8}`}
        fill="none"
        stroke={ACCENT}
        strokeWidth="2"
      />
      <circle cx="220" cy={48 - r1 * 8} r="3" fill={ACCENT} />
    </g>
  );
}

function StagePMotif({ r1 }: { r1: number }) {
  // padlock with shimmer
  return (
    <g transform="translate(120,48)">
      <path
        d="M-12 -10 a 12 12 0 0 1 24 0 v 10"
        fill="none"
        stroke={ACCENT}
        strokeWidth="2.5"
      />
      <rect x="-18" y="0" width="36" height="26" rx="3" fill="#1c2c3c" stroke={ACCENT} strokeWidth="1.5" />
      <circle cx="0" cy="13" r={3 + r1 * 1.5} fill={ACCENT} />
    </g>
  );
}

function InjectMotif({ r1 }: { r1: number }) {
  // exclamation in pulsing ring
  return (
    <g transform="translate(120,48)">
      <circle cx="0" cy="0" r={26 + r1 * 4} fill="none" stroke={ACCENT} strokeWidth="1" opacity="0.4" />
      <circle cx="0" cy="0" r={18} fill="none" stroke={ACCENT} strokeWidth="1.5" />
      <rect x="-2" y="-10" width="4" height="14" fill={ACCENT} rx="1" />
      <circle cx="0" cy="9" r="2.5" fill={ACCENT} />
    </g>
  );
}
